import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData } from "../../../utility/Utility";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

export default function ChatReplyByAdmin() {
  const location = useLocation();
  const navigate = useNavigate();
  const ticket = location.state?.ticketData || {};
  
  const safeTicket = {
    _id: ticket?._id || '',
    title: ticket?.title || 'No title',
    description: ticket?.description || '',
    status: ticket?.status || 'unknown',
    date: ticket?.createdAt || new Date().toISOString(),
    image: ticket?.image || ''
  };

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "" });

  useEffect(() => {
    const loadMessages = async () => {
      setLoading(true);
      const allMsgs = await fetchAllMessages(safeTicket._id);
      setLoading(false);
      if (allMsgs?.length) {
        const formatted = allMsgs.map((m, idx) => ({
          id: idx + 1,
          text: m.message || m.text || '',
          sender: (m.senderType && m.senderType.toLowerCase() === 'admin') ? 'admin' : 'user',
          timestamp: new Date(m.createdAt).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }),
        }));
        setMessages(formatted);
      }
    };
    loadMessages();
  }, [safeTicket._id]);

  const fetchAllMessages = async (ticketId) => {
    try {
      const response = await postData(`api/message/all-messages`, {
        ticketId
      });

      if (response?.success) {
        return response.data; 
      }
      return [];
    } catch (error) {
      console.error("Error fetching messages:", error);
      return [];
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = message.text?.trim();
    if (!trimmed) return;

    try {
      setLoading(true);
      const response = await postData(`api/message/send`, {
        ticketId: safeTicket._id,
        message: trimmed
      });
      setLoading(false);

      if (response?.success) {
        toast.success("Message sent successfully");
        const formattedMsg = {
          id: messages.length + 1,
          text: trimmed,
          sender: "admin",
          timestamp: new Date().toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        };
        setMessages([...messages, formattedMsg]);
        setMessage({ text: "" });
      } else {
        toast.error("Failed to send message");
      }
    } catch {
      setLoading(false);
      toast.error("Error sending message");
    }
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={5000} />
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              <i className="fas fa-arrow-left me-1"></i> Back
            </button>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body p-0">
                  <div className="container-fluid p-0">
                    <div className="row g-0">
                      <div className="col-md-12 p-1 p-md-2">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div>
                            <h6 className="mb-0 fw-normal small">Ticket ID: {safeTicket._id}</h6>
                            <small className="text-muted small">{safeTicket.title}</small>
                          </div>
                        </div>

                        <div className="card border-0 shadow-sm">
                          <div className="card-header bg-white border-bottom py-1">
                            <div className="d-flex align-items-center">
                              <div className="me-1 text-primary">
                                <i className="fas fa-comments small"></i>
                              </div>
                              <div>
                                <h6 className="mb-0 small d-flex align-items-center">
                                  <span className="me-1">{safeTicket.title}</span>
                                  <span className={`badge bg-${safeTicket.status === 'active' ? 'success' : 'secondary'} small`}>
                                    {safeTicket.status || 'open'}
                                  </span>
                                </h6>
                                <small className="text-muted small">Created: {safeTicket.date || 'N/A'}</small>
                              </div>
                            </div>
                          </div>

                          <div className="card-body p-0">
                            <div className="row g-0">
                              <div className="col-md-9">
                                <div className="d-flex flex-column" style={{ height: '350px' }}>
                                  <div className="flex-grow-1 p-1 overflow-auto">
                                    {messages.map((msg) => (
                                      <div
                                        key={msg.id}
                                        className={`mb-1 d-flex ${msg.sender === 'admin' ? 'justify-content-end' : 'justify-content-start'}`}
                                      >
                                        <div
                                          className={`p-1 rounded-1 ${msg.sender === 'admin' ? 'bg-primary text-white' : 'bg-light'} small`}
                                          style={{ maxWidth: '85%' }}
                                        >
                                          <p className="mb-0">{msg.text}</p>
                                          <small className={`d-block text-end ${msg.sender === 'admin' ? 'text-white-50' : 'text-muted'} small`}>
                                            {msg.timestamp}
                                          </small>
                                        </div>
                                      </div>
                                    ))}
                                  </div>

                                  <div className="border-top p-1 bg-white">
                                    <form onSubmit={handleSubmit} className="d-flex align-items-center">
                                      <input
                                        type="text"
                                        className="form-control form-control form-control-sm me-1 py-0 small"
                                        placeholder="Type message..."
                                        value={message.text || ''}
                                        onChange={(e) => setMessage({ text: e.target.value })}
                                      />
                                      <button
                                        type="submit"
                                        className="btn btn-sm btn-primary py-0 px-1 small"
                                        disabled={!message.text?.trim()}
                                      >
                                        <i className="fas fa-paper-plane"></i>
                                      </button>
                                    </form>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}